import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { classroomApi } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';
import { ArrowLeft, FileText, Calendar, Clock, Send, Check, Star } from 'lucide-react';

export default function AssignmentView() {
  const { classId, assignmentId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [assignment, setAssignment] = useState(null);
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [answer, setAnswer] = useState('');
  const [gradingData, setGradingData] = useState({});
  const isTeacher = ['super_admin', 'org_admin', 'faculty'].includes(user?.role);

  useEffect(() => {
    loadData();
  }, [assignmentId]);

  const loadData = async () => {
    try {
      const a = await classroomApi.getAssignment(assignmentId);
      setAssignment(a);
      if (a.my_submission?.content) setAnswer(a.my_submission.content);
      if (isTeacher) {
        const subs = await classroomApi.getSubmissions(assignmentId);
        setSubmissions(subs.submissions || []);
      }
    } catch (e) { toast.error(e.message); }
    finally { setLoading(false); }
  };

  const handleSubmit = async () => {
    if (!answer.trim()) { toast.error('Please write your answer'); return; }
    setSubmitting(true);
    try {
      await classroomApi.submitAssignment(assignmentId, { content: answer });
      toast.success('Submitted!');
      loadData();
    } catch (e) { toast.error(e.message); }
    finally { setSubmitting(false); }
  };

  const handleGrade = async (submissionId) => {
    const data = gradingData[submissionId];
    if (!data?.grade && data?.grade !== 0) { toast.error('Enter a grade'); return; }
    try {
      await classroomApi.gradeSubmission(submissionId, { grade: parseInt(data.grade), feedback: data.feedback || '' });
      toast.success('Graded!');
      loadData();
    } catch (e) { toast.error(e.message); }
  };

  if (loading) return <div className="min-h-screen bg-background flex items-center justify-center"><div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" /></div>;
  if (!assignment) return <div className="min-h-screen bg-background flex items-center justify-center text-muted-foreground">Assignment not found</div>;

  const isPastDue = assignment.due_date && new Date(assignment.due_date) < new Date();
  const isSubmitted = !!assignment.my_submission;
  const isGraded = assignment.my_submission?.status === 'graded';

  return (
    <div className="min-h-screen bg-background" data-testid="assignment-view">
      <header className="sticky top-0 z-30 border-b border-border bg-background/95 backdrop-blur">
        <div className="max-w-4xl mx-auto px-4 py-3 flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate(`/classroom/${classId}`)} data-testid="back-btn">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="flex-1">
            <h1 className="text-lg font-bold text-foreground">{assignment.title}</h1>
            <p className="text-xs text-muted-foreground">{assignment.teacher_name} · {assignment.type}</p>
          </div>
          <Badge variant="outline" className="text-sm">{assignment.points} pts</Badge>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-6">
        {/* Assignment Info */}
        <div className="rounded-xl border border-border bg-card p-6 mb-6">
          <div className="flex items-center gap-4 mb-4">
            <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
              <FileText className="w-6 h-6 text-primary" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-foreground">{assignment.title}</h2>
              <div className="flex items-center gap-3 text-sm text-muted-foreground mt-1">
                {assignment.due_date && (
                  <span className={`flex items-center gap-1 ${isPastDue ? 'text-red-500' : ''}`}>
                    <Calendar className="w-3.5 h-3.5" /> Due: {new Date(assignment.due_date).toLocaleDateString()}
                    {isPastDue && ' (Past due)'}
                  </span>
                )}
                <span className="flex items-center gap-1"><Clock className="w-3.5 h-3.5" /> Posted: {new Date(assignment.created_at).toLocaleDateString()}</span>
              </div>
            </div>
          </div>
          {assignment.instructions && (
            <div className="mt-4 p-4 rounded-lg bg-muted/50">
              <h3 className="text-sm font-semibold text-foreground mb-2">Instructions</h3>
              <p className="text-sm text-foreground/80 whitespace-pre-wrap">{assignment.instructions}</p>
            </div>
          )}
          {assignment.description && (
            <p className="text-sm text-foreground/70 mt-3">{assignment.description}</p>
          )}
        </div>

        {/* Student: Submit Work */}
        {!isTeacher && (
          <div className="rounded-xl border border-border bg-card p-6">
            <h3 className="text-lg font-semibold text-foreground mb-4">
              {isGraded ? 'Your Grade' : isSubmitted ? 'Your Submission' : 'Your Work'}
            </h3>

            {isGraded && (
              <div className="mb-4 p-4 rounded-lg bg-emerald-50 dark:bg-emerald-500/10 border border-emerald-200 dark:border-emerald-500/20">
                <div className="flex items-center gap-2 mb-2">
                  <Check className="w-5 h-5 text-emerald-600" />
                  <span className="text-2xl font-bold text-emerald-700 dark:text-emerald-400">{assignment.my_submission.grade}</span>
                  <span className="text-muted-foreground">/ {assignment.points}</span>
                </div>
                {assignment.my_submission.feedback && (
                  <div className="mt-2">
                    <p className="text-sm font-medium text-foreground">Teacher Feedback:</p>
                    <p className="text-sm text-foreground/80">{assignment.my_submission.feedback}</p>
                  </div>
                )}
              </div>
            )}

            <Textarea
              defaultValue={answer}
              onBlur={e => setAnswer(e.target.value)}
              placeholder="Write your answer here..."
              className="min-h-[200px] mb-4"
              disabled={isGraded}
              data-testid="assignment-answer"
            />
            {!isGraded && (
              <div className="flex items-center justify-between">
                {isSubmitted && <Badge variant="secondary" className="flex items-center gap-1"><Check className="w-3 h-3" /> Submitted</Badge>}
                <Button onClick={handleSubmit} disabled={submitting || isGraded} className="ml-auto" data-testid="submit-assignment-btn">
                  <Send className="w-4 h-4 mr-1" /> {isSubmitted ? 'Resubmit' : 'Submit'}
                </Button>
              </div>
            )}
          </div>
        )}

        {/* Teacher: View & Grade Submissions */}
        {isTeacher && (
          <div className="rounded-xl border border-border bg-card p-6">
            <h3 className="text-lg font-semibold text-foreground mb-4">
              Student Submissions ({submissions.length})
            </h3>
            {submissions.length === 0 ? (
              <p className="text-muted-foreground py-4">No submissions yet.</p>
            ) : (
              <div className="space-y-4">
                {submissions.map(sub => (
                  <div key={sub.submission_id} className="p-4 rounded-xl border border-border bg-muted/30" data-testid={`submission-${sub.submission_id}`}>
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-sm font-bold text-primary">
                          {sub.student_name?.[0]?.toUpperCase()}
                        </div>
                        <div>
                          <p className="text-sm font-semibold text-foreground">{sub.student_name}</p>
                          <p className="text-xs text-muted-foreground">Submitted: {new Date(sub.submitted_at).toLocaleString()}</p>
                        </div>
                      </div>
                      {sub.status === 'graded' && (
                        <Badge className="bg-emerald-600">{sub.grade}/{assignment.points}</Badge>
                      )}
                    </div>
                    <div className="p-3 rounded-lg bg-background border border-border mb-3">
                      <p className="text-sm text-foreground whitespace-pre-wrap">{sub.content}</p>
                    </div>
                    {sub.status !== 'graded' ? (
                      <div className="flex items-end gap-3">
                        <div className="flex-1">
                          <label className="text-xs font-medium text-foreground">Grade (/{assignment.points})</label>
                          <Input type="number" min={0} max={assignment.points}
                            value={gradingData[sub.submission_id]?.grade || ''}
                            onChange={e => setGradingData(p => ({...p, [sub.submission_id]: {...p[sub.submission_id], grade: e.target.value}}))}
                            placeholder="Score" className="h-8" />
                        </div>
                        <div className="flex-[2]">
                          <label className="text-xs font-medium text-foreground">Feedback</label>
                          <Input value={gradingData[sub.submission_id]?.feedback || ''}
                            onChange={e => setGradingData(p => ({...p, [sub.submission_id]: {...p[sub.submission_id], feedback: e.target.value}}))}
                            placeholder="Optional feedback" className="h-8" />
                        </div>
                        <Button size="sm" onClick={() => handleGrade(sub.submission_id)} data-testid={`grade-btn-${sub.submission_id}`}>
                          <Check className="w-3.5 h-3.5 mr-1" /> Grade
                        </Button>
                      </div>
                    ) : (
                      <div className="text-xs text-muted-foreground">
                        Graded: {sub.grade}/{assignment.points} {sub.feedback && `· ${sub.feedback}`}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </main>
    </div>
  );
}
